import React, { useEffect, useState } from 'react';
import { BudgetVisualization } from './BudgetVisualization.tsx';
import { ArrowRight } from 'lucide-react';
import { getBudget } from '../../../utils/budget.ts';

interface BudgetSummaryProps {
  onNavigateToBudget: () => void;
}

export const BudgetSummary: React.FC<BudgetSummaryProps> = ({ onNavigateToBudget }) => {
  const [budget, setBudget] = useState<number>(0);
  const [categories, setCategories] = useState<any[]>([]);

  useEffect(() => {
    const fetchBudgetData = async () => {
      try {
        const budgetSections = await getBudget();
        const totalCost = budgetSections.reduce((acc, item) => acc + (item.cost || 0), 0);
        
        // Transform budgetSections to include colors for visualization
        const colorPalette = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4'];
        const transformedCategories = budgetSections.map((item, index) => ({
            id: item.id, 
            name: item.name,
            percentage: item.percentage,
          color: colorPalette[index % colorPalette.length]
        })); 

        setBudget(totalCost);
        setCategories(transformedCategories); 
      } catch (error) {
        console.error('Error fetching budget:', error);
      }
    };

    fetchBudgetData();
  }, []);

  const topCategories = categories
    .sort((a, b) => b.percentage - a.percentage)
    .slice(0, 4);

  return (
    <div className="space-y-6">
      {/* Budget Total */}
      <div className="text-center">
        <div className="text-3xl font-bold text-gray-900 mb-1">
            ${budget.toLocaleString()}
        </div>
        <p className="text-sm text-gray-500">Total Budget</p>
      </div>

      {/* Top Categories */}
      {topCategories.length > 0 && (
      <div>
          <h5 className="text-sm font-medium text-gray-900 mb-3">Top Categories</h5>
          <div className="space-y-3">
          {topCategories.map(category => {
            const amount = Math.round((category.percentage / 100) * budget);
            return (
                <div key={category.id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: category.color }}
                    />
                    <span className="text-sm text-gray-600">{category.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                      ${amount.toLocaleString()}
                  </div>
                    <div className="text-xs text-gray-500">
                      {category.percentage.toFixed(1)}%
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Mini Visualization */}
      {categories.length > 0 && (
        <div className="border-t border-gray-200 pt-4">
      <BudgetVisualization categories={categories} />
        </div>
      )}
      
      {/* Navigate Button */}
      <button
        onClick={onNavigateToBudget}
        className="w-full flex items-center justify-center space-x-2 px-4 py-2 
          border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300
          transition-all duration-200 group"
      >
        <span className="text-sm font-medium text-gray-700">View Full Budget</span>
        <ArrowRight className="w-4 h-4 text-gray-500 transform group-hover:translate-x-1 transition-transform" />
      </button>
    </div>
  );
};